import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, put, del } from '@/lib/api'

// ==================== Types ====================

export type CategoryType = 'income' | 'expense'

export interface Category {
  id: string
  name: string
  type: CategoryType
  parentId: string | null
  description: string | null
  sortOrder: number
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface CategoryWithChildren extends Category {
  children?: CategoryWithChildren[]
}

export interface CategoryTree {
  income: CategoryWithChildren[]
  expense: CategoryWithChildren[]
}

export interface CreateCategoryInput {
  name: string
  type: CategoryType
  parentId?: string
  description?: string
  sortOrder?: number
}

export interface UpdateCategoryInput {
  name?: string
  description?: string
  sortOrder?: number
}

// ==================== Query Keys ====================

export const categoryKeys = {
  all: ['categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: () => [...categoryKeys.lists()] as const,
  detail: (id: string) => [...categoryKeys.all, 'detail', id] as const,
}

// ==================== Hooks ====================

/**
 * 获取分类列表（树形结构）
 */
export function useCategories() {
  return useQuery({
    queryKey: categoryKeys.list(),
    queryFn: () => get<CategoryTree>('/finance/categories'),
  })
}

/**
 * 获取分类详情
 */
export function useCategory(id: string) {
  return useQuery({
    queryKey: categoryKeys.detail(id),
    queryFn: () => get<Category>(`/finance/categories/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建分类
 */
export function useCreateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCategoryInput) => post<Category>('/finance/categories', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: categoryKeys.lists() })
    },
  })
}

/**
 * 更新分类
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryInput }) =>
      put<Category>(`/finance/categories/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: categoryKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: categoryKeys.lists() })
    },
  })
}

/**
 * 删除分类
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/finance/categories/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: categoryKeys.lists() })
    },
  })
}
